import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './CalculatorAdmin.css';

const CalculatorAdmin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // Estado para estadísticas
  const [stats, setStats] = useState({
    totalProducts: '-',
    totalProviders: '-',
    multiplicador: '-',
    antireflejoProducts: []
  });

  // Estado para formulario de carga
  const [uploadForm, setUploadForm] = useState({
    proveedor: '',
    file: null,
    fileName: ''
  });

  // Estado para formulario de configuración
  const [configForm, setConfigForm] = useState({
    multiplicador: ''
  });

  const API_URL = '/api';

  useEffect(() => {
    loadStats();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 5000);
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar estadísticas');
      }

      const result = await response.json();

      setStats({
        totalProducts: result.total_productos,
        totalProviders: result.proveedores.length,
        multiplicador: result.multiplicador,
        antireflejoProducts: result.antireflejo_productos || []
      });

      setConfigForm({
        multiplicador: result.multiplicador
      });
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      showAlert('Error al cargar estadísticas', 'error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({
        ...uploadForm,
        file: file,
        fileName: file.name
      });
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('proveedor', uploadForm.proveedor);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Error al cargar archivo');
      }

      showAlert(`Éxito: ${result.message}`, 'success');
      setUploadForm({ proveedor: '', file: null, fileName: '' });
      document.getElementById('fileInput').value = '';
      loadStats();
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('multiplicador', configForm.multiplicador);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Error al actualizar configuración');
      }

      showAlert(result.message, 'success');
      loadStats();
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="calculator-admin">
      <nav className="dashboard-nav">
        <div className="container">
          <div className="nav-content">
            <div className="nav-brand">
              <h2>COL2C</h2>
            </div>
            <div className="nav-user">
              <span className="user-name">
                {user?.firstName} {user?.lastName}
              </span>
              <span className="user-role">{user?.role}</span>
              <button onClick={() => navigate('/dashboard')} className="btn btn-secondary btn-sm">
                Volver al Dashboard
              </button>
              <button onClick={handleLogout} className="btn btn-secondary btn-sm">
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="calculator-admin-content">
        <div className="container">
          <div className="calculator-header">
            <h1>Panel de Administración - Calculadora</h1>
            <p>Gestión de la Calculadora de Lentes</p>
          </div>

          {alert.show && (
            <div className={`alert alert-${alert.type}`}>
              {alert.message}
            </div>
          )}

          {/* Estadísticas */}
          <div className="card">
            <h2>Estadísticas del Sistema</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>{stats.totalProducts}</h3>
                <p>Total de Productos</p>
              </div>
              <div className="stat-card">
                <h3>{stats.totalProviders}</h3>
                <p>Proveedores</p>
              </div>
              <div className="stat-card">
                <h3>{stats.multiplicador}</h3>
                <p>Multiplicador Actual</p>
              </div>
            </div>

            {stats.antireflejoProducts.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ color: '#667eea', marginBottom: '10px' }}>Productos de Antireflejo</h3>
                <div className="antireflejo-list">
                  {stats.antireflejoProducts.map((product, index) => (
                    <div key={index} className="antireflejo-item">
                      <strong>{product.nombre}</strong> ({product.codigo})<br />
                      <span style={{ color: '#666' }}>Precio base: ${product.precio_base.toFixed(2)} →
                      Precio calculado: ${product.precio_calculado.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Formulario de carga */}
          <div className="card">
            <h2>Cargar Lista de Precios</h2>
            <form onSubmit={handleUploadSubmit}>
              <div className="form-group">
                <label htmlFor="proveedor">Nombre del Proveedor</label>
                <input
                  type="text"
                  id="proveedor"
                  value={uploadForm.proveedor}
                  onChange={(e) => setUploadForm({ ...uploadForm, proveedor: e.target.value })}
                  placeholder="Ej: FF Tech"
                  required
                />
                <small>Si el proveedor ya existe, se reemplazarán sus productos</small>
              </div>

              <div className="form-group">
                <label>Archivo Excel/CSV</label>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id="fileInput"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    required
                  />
                  <label htmlFor="fileInput" className="file-input-label">
                    Haz clic para seleccionar archivo
                  </label>
                </div>
                {uploadForm.fileName && (
                  <div className="file-name">Archivo: {uploadForm.fileName}</div>
                )}
              </div>

              <button type="submit" className="btn" disabled={loading}>
                {loading ? 'Cargando...' : 'Cargar Productos'}
              </button>
            </form>
          </div>

          {/* Formulario de configuración */}
          <div className="card">
            <h2>Configuración de Precios</h2>
            <form onSubmit={handleConfigSubmit}>
              <div className="form-group">
                <label htmlFor="multiplicador">Multiplicador de Precios</label>
                <input
                  type="number"
                  id="multiplicador"
                  value={configForm.multiplicador}
                  onChange={(e) => setConfigForm({ multiplicador: e.target.value })}
                  step="0.1"
                  min="1"
                  placeholder="2.5"
                  required
                />
                <small>Este valor se multiplica por el precio unitario de todos los productos (incluyendo antireflejo). No es visible para el cliente.</small>
              </div>

              <button type="submit" className="btn" disabled={loading}>
                Actualizar Multiplicador
              </button>
            </form>

            <div style={{ marginTop: '20px', padding: '15px', background: '#e7f0ff', borderRadius: '8px' }}>
              <strong style={{ color: '#667eea' }}>Nota sobre Antireflejo:</strong>
              <p style={{ marginTop: '10px', color: '#666', fontSize: '0.9rem' }}>
                Los productos de antireflejo (Guardian S000126 y Guardian B-Block S000128) deben estar cargados
                en el sistema como cualquier otro producto. Sus precios se multiplicarán automáticamente por el
                factor configurado arriba.
              </p>
            </div>
          </div>

          {/* Información del formato */}
          <div className="card">
            <h2>Formato del Archivo Excel</h2>
            <p style={{ marginBottom: '15px' }}>El archivo debe contener las siguientes columnas:</p>
            <ul style={{ lineHeight: '2', color: '#666' }}>
              <li><strong>Categoría:</strong> Visión Sencilla, Multifocal, Bifocal, etc.</li>
              <li><strong>Linea:</strong> Nombre de la línea de producto</li>
              <li><strong>Diseño:</strong> Tipo específico de lente</li>
              <li><strong>Codigo:</strong> Código del producto</li>
              <li><strong>Rango:</strong> Descripción del rango (texto)</li>
              <li><strong>esf_min:</strong> Esfera mínima (número)</li>
              <li><strong>esf_max:</strong> Esfera máxima (número)</li>
              <li><strong>cil_max:</strong> Cilindro máximo (número negativo)</li>
              <li><strong>Precio_Unit:</strong> Precio unitario del proveedor</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculatorAdmin;
